import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Badge } from '@ui-library/badge';
import { SmallText } from '@ui-library/typography';
import type { TrialPhase } from '../types';

interface TrialBaseBadgeProps {
  label: string;
  trialPhase?: TrialPhase;
  bgColor?: string;
  icon?: JSX.Element;
  compact?: boolean;
}

const CompactLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  white-space: nowrap;
`;

const useBadgeColor = (trialPhase?: TrialPhase) => {
  const theme = useTheme();
  const TRIAL_BADGE_COLORS: Record<Exclude<TrialPhase, 'hard_stop'>, string> = {
    active_safe: theme.token['yellow.4'],
    active_warning: theme.token['orange.3'],
    active_critical: theme.token['red.3'],
    grace_period: theme.token['red.3'],
  };
  if (trialPhase && trialPhase !== 'hard_stop') {
    return TRIAL_BADGE_COLORS[trialPhase];
  }
  return theme.token['yellow.4'];
};

/**
 * Core trial badge primitive. Renders a color-coded pill based on trial phase.
 * All higher-level trial badges (TrialBadge, FeatureGateBadge, TrialPlanBadge) compose this.
 *
 * - Color is derived from `trialPhase` via `getTrialBadgeColor`, or overridden with `bgColor`.
 * - Supports a compact size (10px font) for tight spaces like sidebar headers.
 */
export const TrialBaseBadge: React.FC<TrialBaseBadgeProps> = ({
  label,
  trialPhase,
  bgColor,
  icon,
  compact = false,
}) => {
  const color = useBadgeColor(trialPhase);

  return (
    <Badge
      size="small"
      bgColor={bgColor || color}
      icon={icon}
      label={
        compact ? (
          <CompactLabel>{label}</CompactLabel>
        ) : (
          <SmallText style={{ whiteSpace: 'nowrap' }} fontWeight={500}>
            {label}
          </SmallText>
        )
      }
    />
  );
};
